import { computed, ref, watch } from "vue";
import { useMastheadImageUrls } from "@/composables/useMastheadImageUrls.js";

/**
 * The header image an expert-list card shows in place of its item photo
 * (GH-1535): the list's masthead photo on the owner's focal point, as the
 * Expert Lists grid card (GH-1299) and the Gift Ideas feature card
 * (GH-1485) already do. Null when the list is not an expert list, has no
 * header image, or the image 404s — the card then falls back to whatever
 * it showed before.
 *
 * @param {() => object} getList  Reactive getter for the list payload
 *   (`type`, `masthead_photo`, `masthead_focus_x`, `masthead_focus_y`).
 * @returns {{ headerImage: import("vue").ComputedRef<object|null>, headerFailed: import("vue").Ref<boolean> }}
 */
export function useExpertListHeaderImage(getList) {
    const headerFailed = ref(false);

    // A new photo gets a fresh chance after an earlier one 404ed.
    watch(
        () => getList()?.masthead_photo,
        () => {
            headerFailed.value = false;
        },
    );

    const headerImage = computed(() => {
        const list = getList() ?? {};

        if (
            list.type !== "expert" ||
            !list.masthead_photo ||
            headerFailed.value
        ) {
            return null;
        }

        const focus =
            Number.isFinite(list.masthead_focus_x) &&
            Number.isFinite(list.masthead_focus_y)
                ? { x: list.masthead_focus_x, y: list.masthead_focus_y }
                : null;

        return useMastheadImageUrls(list.masthead_photo, focus);
    });

    return { headerImage, headerFailed };
}
